/**
 * <video-hero>
 *
 * Background video for the hero section. The <source> elements are attached
 * here rather than rendered server-side, so the file is only ever downloaded
 * when we actually intend to play it:
 *
 *   - never, if the visitor prefers reduced motion
 *   - never, on small screens when the merchant has switched mobile video off
 *   - paused while scrolled out of view, to keep it off the main thread
 *
 * Until (or unless) the video plays, the section's poster image is what shows,
 * so the hero is complete without JavaScript.
 */
if (!customElements.get('video-hero')) {
  customElements.define(
    'video-hero',
    class VideoHero extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.smallScreen = window.matchMedia('(max-width: 749px)');

        if (!this.shouldPlay()) return;

        this.load();

        this.video.addEventListener(
          'canplay',
          () => {
            this.classList.add('is-playing');
            this.play();
          },
          { once: true }
        );

        // Don't burn cycles decoding video the visitor can't see.
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.video.pause()));
        });
        this.observer.observe(this);

        this.onMotionChange = () => {
          if (this.reducedMotion.matches) {
            this.video.pause();
            this.classList.remove('is-playing');
          } else {
            this.play();
          }
        };
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      }

      shouldPlay() {
        if (this.reducedMotion.matches) return false;
        if (this.smallScreen.matches && this.dataset.allowMobile !== 'true') return false;
        return true;
      }

      load() {
        let sources;
        try {
          sources = JSON.parse(this.dataset.sources || '[]');
        } catch {
          return;
        }

        sources.forEach(({ src, type }) => {
          const source = document.createElement('source');
          source.src = src;
          if (type) source.type = type;
          this.video.appendChild(source);
        });

        this.video.load();
      }

      play() {
        // Muted playback is allowed to autoplay, but a rejected promise here
        // is normal (battery saver, background tab) and simply leaves the
        // poster in place.
        this.video.play?.().catch(() => {});
      }
    }
  );
}
